import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api, { BACKEND_URL } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Separator } from "../components/ui/separator";
import { Plus, Minus, CalendarIcon, Upload, Search, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

// Measurement definitions
const SHIRT_MEASUREMENTS = ["Length", "Chest", "Waist", "Hip", "Shoulder", "Sleeves", "Biceps", "Neck"];
const KURTA_MEASUREMENTS = ["Length", "Chest", "Waist", "Hip", "Shoulder", "Sleeves", "Biceps", "Neck", "Mori"];
const COAT_MEASUREMENTS = ["Length", "Chest", "Waist", "Hip", "Shoulder", "Sleeves", "Biceps", "Neck", "Jacket Length", "Cross Back", "Mori"];
const PRINCE_COAT_MEASUREMENTS = ["Length", "Chest", "Waist", "Hip", "Shoulder", "Sleeves", "Biceps", "Neck", "Cross Back", "Mori"];
const LOWER_BODY_MEASUREMENTS = ["Length", "Waist", "Hip", "Thigh", "Knee", "Calf", "Bottom", "U"];
const PANT_MEASUREMENTS = ["Length", "Waist", "Hip", "Thigh", "Knee", "Calf", "Bottom", "U"];
const PAJAMA_MEASUREMENTS = ["Length", "Waist", "Hip", "Thigh", "Bottom", "U"];

const CLOTHING_TYPES = [
  { value: "shirt", label: "Shirt", measurements: SHIRT_MEASUREMENTS },
  { value: "coat", label: "Coat", measurements: COAT_MEASUREMENTS },
  { value: "prince_coat", label: "Prince Coat", measurements: PRINCE_COAT_MEASUREMENTS },
  { value: "sherwani", label: "Sherwani", measurements: COAT_MEASUREMENTS },
  { value: "kurta", label: "Kurta", measurements: KURTA_MEASUREMENTS },
  { value: "short_kurta", label: "Short Kurta", measurements: KURTA_MEASUREMENTS },
  { value: "pathani", label: "Pathani", measurements: KURTA_MEASUREMENTS },
  { value: "waistcoat", label: "Waistcoat", measurements: SHIRT_MEASUREMENTS },
  { value: "modi_jacket", label: "Modi Jacket", measurements: SHIRT_MEASUREMENTS },
  { value: "blazer", label: "Blazer", measurements: COAT_MEASUREMENTS },
  { value: "pant", label: "Pant", measurements: PANT_MEASUREMENTS },
  { value: "trouser", label: "Trouser", measurements: PANT_MEASUREMENTS },
  { value: "pajama", label: "Pajama", measurements: PAJAMA_MEASUREMENTS },
  { value: "shalwar", label: "Shalwar", measurements: LOWER_BODY_MEASUREMENTS },
  { value: "police_uniform", label: "Police Uniform", measurements: SHIRT_MEASUREMENTS },
];

// Map the public booking-form labels (CLOTHING_OPTIONS on LandingPage) to the
// internal slip clothing_type values above. "Other" / unknown falls through to
// an empty string so the admin can pick a type manually.
const PUBLIC_LABEL_TO_INTERNAL = {
  "Shirt": "shirt",
  "Suit & Coat": "coat",
  "Sherwani": "sherwani",
  "Kurta": "kurta",
  "Pathani": "pathani",
  "Blazer": "blazer",
  "Waistcoat": "waistcoat",
  "Trousers & Pants": "pant",
};

const buildItemFromInquiry = (inqItem) => {
  const typeValue = PUBLIC_LABEL_TO_INTERNAL[inqItem?.clothing_type] || "";
  const clothing = CLOTHING_TYPES.find(c => c.value === typeValue);
  const measurements = {};
  (clothing?.measurements || []).forEach(k => { measurements[k] = ""; });
  return {
    clothing_type: typeValue,
    quantity: 1,
    measurements,
    notes: inqItem?.message || "",
  };
};

export default function CreateSlipPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Pre-fill from inquiry (when coming from Requested Customers page).
  // Full inquiry (with items[] + address) is passed via router state;
  // name/phone remain in the URL as a lightweight fallback.
  const inquiry = location.state?.inquiry || null;
  const prefillName = inquiry?.name || searchParams.get("name") || "";
  const prefillPhone = inquiry?.phone || searchParams.get("phone") || "";
  const prefillAddress = inquiry?.address || "";
  // Derive inquiry items (prefer new items[], fall back to legacy single-item shape)
  const inquiryItems = inquiry
    ? (Array.isArray(inquiry.items) && inquiry.items.length > 0
        ? inquiry.items
        : (inquiry.clothing_type ? [{ clothing_type: inquiry.clothing_type, message: inquiry.message || "" }] : []))
    : [];

  const [customers, setCustomers] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState(prefillName);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: prefillName, phone: prefillPhone, address: prefillAddress });

  const [selectedWorker, setSelectedWorker] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(undefined);
  const [referencePhoto, setReferencePhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [items, setItems] = useState(() => {
    // Seed items from the inquiry's requested clothing list, if present.
    if (inquiryItems.length > 0) {
      return inquiryItems.map(buildItemFromInquiry);
    }
    return [{ clothing_type: "", quantity: 1, measurements: {}, notes: "" }];
  });

  // Tracks whether we've already attempted phone-based auto-match on the
  // inquiry prefill (runs once customers list arrives).
  const [autoMatchDone, setAutoMatchDone] = useState(false);

  // Saved measurements+notes from last order for selected customer
  const [savedData, setSavedData] = useState({});

  // When saved data arrives, retroactively fill any already-selected clothing types
  useEffect(() => {
    if (!Object.keys(savedData).length) return;
    setItems(prev => prev.map(item => {
      if (!item.clothing_type) return item;
      const saved = savedData[item.clothing_type];
      if (!saved) return item;
      const clothing = CLOTHING_TYPES.find(c => c.value === item.clothing_type);
      const keys = clothing ? clothing.measurements : [];
      const updatedMeasurements = { ...item.measurements };
      keys.forEach(k => {
        if (!updatedMeasurements[k] && saved.measurements?.[k]) {
          updatedMeasurements[k] = saved.measurements[k];
        }
      });
      return { ...item, measurements: updatedMeasurements, notes: item.notes || saved.notes || "" };
    }));
  }, [savedData]);

  useEffect(() => {
    api.get("/customers").then(res => setCustomers(res.data)).catch(() => {});
    api.get("/workers", { params: { active_only: true } }).then(res => setWorkers(res.data)).catch(() => {});
  }, []);

  // When arriving from a "Requested Customer" inquiry, try to bind the slip
  // to an existing customer by matching the phone number exactly. If found,
  // we select that profile (saved measurements auto-prefill via selectCustomer).
  // Otherwise we open the "new customer" form with the inquiry's details.
  useEffect(() => {
    if (autoMatchDone) return;
    if (!(prefillName || prefillPhone)) return;
    if (customers.length === 0) return; // wait for the list to load

    const normalize = s => (s || "").replace(/\D/g, "").slice(-10);
    const target = normalize(prefillPhone);
    const match = target ? customers.find(c => normalize(c.phone) === target) : null;

    if (match) {
      selectCustomer(match);
    } else {
      setShowNewCustomer(true);
    }
    setAutoMatchDone(true);
  }, [customers, autoMatchDone, prefillName, prefillPhone]);

  useEffect(() => {
    if (customerSearch.length > 0 && !selectedCustomer && !showNewCustomer) {
      const filtered = customers.filter(c =>
        c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
        c.phone.includes(customerSearch)
      );
      setFilteredCustomers(filtered);
      setShowCustomerDropdown(true);
    } else {
      setFilteredCustomers([]);
      setShowCustomerDropdown(false);
    }
  }, [customerSearch, customers, selectedCustomer, showNewCustomer]);

  const selectCustomer = (c) => {
    setSelectedCustomer(c);
    setCustomerSearch(c.name);
    setShowCustomerDropdown(false);
    setShowNewCustomer(false);
    // Fetch saved measurements+notes for this returning customer
    api.get(`/customers/${c.id}/saved-measurements`)
      .then(r => setSavedData(r.data))
      .catch(() => setSavedData({}));
  };

  const startNewCustomer = () => {
    setShowNewCustomer(true);
    setSelectedCustomer(null);
    setCustomerSearch("");
    setShowCustomerDropdown(false);
  };

  const cancelNewCustomer = () => {
    setShowNewCustomer(false);
    setNewCustomer({ name: "", phone: "", address: "" });
  };

  const addItem = () => {
    setItems([...items, { clothing_type: "", quantity: 1, measurements: {}, notes: "" }]);
  };

  const removeItem = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItemType = (index, typeValue) => {
    const newItems = [...items];
    const clothing = CLOTHING_TYPES.find(c => c.value === typeValue);
    const keys = clothing ? clothing.measurements : [];
    const saved = savedData[typeValue] || {};
    const savedMeasurements = saved.measurements || {};
    const savedNotes = saved.notes || "";
    const measurements = {};
    keys.forEach(k => { measurements[k] = savedMeasurements[k] !== undefined ? savedMeasurements[k] : ""; });
    newItems[index] = { ...newItems[index], clothing_type: typeValue, measurements, notes: newItems[index].notes || savedNotes };
    setItems(newItems);
    setTimeout(() => {
      const firstInput = document.querySelector(`[data-measurement="${index}-0"]`);
      if (firstInput) firstInput.focus();
    }, 100);
  };

  const updateQuantity = (index, delta) => {
    const newItems = [...items];
    const current = newItems[index].quantity || 1;
    newItems[index].quantity = Math.max(1, current + delta);
    setItems(newItems);
  };

  const setQuantity = (index, value) => {
    const newItems = [...items];
    const num = parseInt(value);
    newItems[index].quantity = isNaN(num) || num < 1 ? 1 : num;
    setItems(newItems);
  };

  const updateMeasurement = (itemIdx, key, value) => {
    const newItems = [...items];
    newItems[itemIdx].measurements[key] = value;
    setItems(newItems);
  };

  const handleMeasurementKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const allInputs = document.querySelectorAll('[data-measurement]');
      const inputsArray = Array.from(allInputs);
      const currentIndex = inputsArray.findIndex(input => input === e.target);
      if (currentIndex !== -1 && currentIndex < inputsArray.length - 1) {
        inputsArray[currentIndex + 1].focus();
        inputsArray[currentIndex + 1].select();
      }
    }
  };

  const updateNotes = (index, notes) => {
    const newItems = [...items];
    newItems[index].notes = notes;
    setItems(newItems);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setReferencePhoto(res.data.url);
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const handleSubmit = async () => {
    // Validate
    if (!selectedCustomer && !showNewCustomer) {
      toast.error("Please select or add a customer");
      return;
    }
    if (showNewCustomer && (!newCustomer.name || !newCustomer.phone)) {
      toast.error("Please enter customer name and phone");
      return;
    }
    if (!items[0].clothing_type) {
      toast.error("Please add at least one clothing item");
      return;
    }

    setSaving(true);
    try {
      let customerId = selectedCustomer?.id;

      // Create new customer if needed
      if (showNewCustomer) {
        const customerRes = await api.post("/customers", {
          name: newCustomer.name,
          phone: newCustomer.phone,
          address: newCustomer.address || null
        });
        customerId = customerRes.data.id;
        toast.success("Customer created");
      }

      // Create slip
      const payload = {
        customer_id: customerId,
        items: items.map(item => ({
          clothing_type: item.clothing_type,
          quantity: item.quantity || 1,
          measurements: item.measurements,
          notes: item.notes
        })),
        worker_id: selectedWorker || null,
        delivery_date: deliveryDate ? format(deliveryDate, "yyyy-MM-dd") : null,
        reference_photo: referencePhoto
      };
      const res = await api.post("/slips", payload);
      toast.success(`Slip ${res.data.slip_number} created`);
      navigate(`/slips/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create slip");
    }
    setSaving(false);
  };

  return (
    <div data-testid="create-slip-page" className="space-y-4 md:space-y-6 max-w-4xl">
      <div className="animate-fade-in-up">
        <h1 className="font-serif text-2xl md:text-3xl font-semibold text-foreground">New Slip</h1>
        <p className="text-muted-foreground text-sm mt-1">Create a new tailoring order</p>
      </div>

      {/* Customer Section */}
      <Card className="luxury-card rounded-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-serif text-base">Customer</CardTitle>
            {!showNewCustomer && !selectedCustomer && (
              <Button variant="ghost" size="sm" onClick={startNewCustomer} className="text-xs gold-text gap-1">
                <UserPlus className="w-3 h-3" /> New Customer
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {showNewCustomer ? (
            // New Customer Form
            <div className="space-y-4">
              <div className="p-4 border border-dashed border-[#D4AF37]/30 rounded-sm bg-[#D4AF37]/5">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">New Customer Details</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Name *</Label>
                    <Input
                      value={newCustomer.name}
                      onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                      placeholder="Customer name"
                      className="bg-background border-border rounded-sm h-11"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Phone *</Label>
                    <Input
                      value={newCustomer.phone}
                      onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                      placeholder="Phone number"
                      className="bg-background border-border rounded-sm h-11"
                      inputMode="tel"
                    />
                  </div>
                  <div className="md:col-span-2 space-y-1">
                    <Label className="text-xs text-muted-foreground">Address</Label>
                    <Input
                      value={newCustomer.address}
                      onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                      placeholder="Address (optional)"
                      className="bg-background border-border rounded-sm h-11"
                    />
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={cancelNewCustomer} className="mt-3 text-xs text-muted-foreground">
                  Cancel - Search Existing Customer
                </Button>
              </div>
            </div>
          ) : (
            // Search Existing Customer
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={customerSearch}
                onChange={(e) => { setCustomerSearch(e.target.value); setSelectedCustomer(null); }}
                placeholder="Search customer by name or phone..."
                className="pl-10 bg-background border-border rounded-sm h-11"
              />
              {showCustomerDropdown && filteredCustomers.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-card border border-border rounded-sm shadow-lg max-h-48 overflow-y-auto">
                  {filteredCustomers.map(c => (
                    <button key={c.id} onClick={() => selectCustomer(c)} className="w-full text-left px-4 py-3 hover:bg-white/[0.04] transition-colors border-b border-border last:border-0">
                      <p className="text-sm text-foreground">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.phone}</p>
                    </button>
                  ))}
                </div>
              )}
              {selectedCustomer && (
                <div className="mt-3 p-3 bg-background rounded-sm border border-border flex items-center justify-between">
                  <div>
                    <p className="text-sm text-foreground font-medium">{selectedCustomer.name}</p>
                    <p className="text-xs text-muted-foreground">{selectedCustomer.phone}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedCustomer(null); setCustomerSearch(""); setSavedData({}); }} className="text-xs text-muted-foreground">
                    Change
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clothing Items */}
      {items.map((item, idx) => (
        <Card key={idx} className="luxury-card rounded-sm">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="font-serif text-base">Item {idx + 1}</CardTitle>
            {items.length > 1 && (
              <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="text-xs text-red-400 hover:text-red-300">
                Remove
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Type & Quantity */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Clothing Type</Label>
                <Select value={item.clothing_type} onValueChange={(v) => updateItemType(idx, v)}>
                  <SelectTrigger className="bg-background border-border rounded-sm h-11">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {CLOTHING_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Quantity</Label>
                <div className="flex items-center gap-1 h-11">
                  <Button 
                    type="button"
                    variant="outline" 
                    size="icon" 
                    onClick={() => updateQuantity(idx, -1)}
                    className="h-11 w-11 border-border rounded-sm flex-shrink-0"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={item.quantity}
                    onChange={(e) => setQuantity(idx, e.target.value)}
                    onFocus={(e) => e.target.select()}
                    className="w-14 sm:w-16 h-11 bg-background border border-border rounded-sm text-center text-sm text-foreground focus:border-[rgba(212,175,55,0.5)] outline-none"
                  />
                  <Button 
                    type="button"
                    variant="outline" 
                    size="icon" 
                    onClick={() => updateQuantity(idx, 1)}
                    className="h-11 w-11 border-border rounded-sm flex-shrink-0"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Measurements - Fixed fields, no delete */}
            {Object.keys(item.measurements).length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Measurements (Press Enter for next)</Label>
                  {item.clothing_type && savedData[item.clothing_type] && (
                    <span className="text-[10px] text-green-400 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                      Pre-filled from last order
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.keys(item.measurements).map((key, keyIdx) => (
                    <div key={key} className="space-y-1">
                      <label className="text-xs text-muted-foreground">{key}</label>
                      <input
                        data-measurement={`${idx}-${keyIdx}`}
                        type="text"
                        inputMode="decimal"
                        value={item.measurements[key]}
                        onChange={(e) => updateMeasurement(idx, key, e.target.value)}
                        onKeyDown={handleMeasurementKeyDown}
                        onFocus={(e) => e.target.select()}
                        placeholder="0"
                        className="w-full bg-background border border-border rounded-sm h-11 px-3 text-sm text-foreground focus:border-[rgba(212,175,55,0.5)] outline-none"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Special Notes</Label>
                {item.clothing_type && savedData[item.clothing_type]?.notes && item.notes === savedData[item.clothing_type]?.notes && (
                  <span className="text-[10px] text-green-400 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                    From last order
                  </span>
                )}
              </div>
              <textarea
                value={item.notes}
                onChange={(e) => updateNotes(idx, e.target.value)}
                placeholder="Any special instructions..."
                className="w-full h-20 bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground resize-none focus:border-[rgba(212,175,55,0.5)] outline-none"
              />
            </div>
          </CardContent>
        </Card>
      ))}

      <Button variant="outline" onClick={addItem} className="w-full border-dashed border-border rounded-sm gap-2 h-12">
        <Plus className="w-4 h-4" /> Add Another Item
      </Button>

      <Separator className="opacity-30" />

      {/* Worker & Delivery */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="luxury-card rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-serif text-base">Assign Worker</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedWorker} onValueChange={setSelectedWorker}>
              <SelectTrigger className="bg-background border-border rounded-sm h-11">
                <SelectValue placeholder="Select worker (optional)" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {workers.map(w => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card className="luxury-card rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-serif text-base">Delivery Date</CardTitle>
          </CardHeader>
          <CardContent>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start bg-background border-border rounded-sm h-11">
                  <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                  {deliveryDate ? format(deliveryDate, "dd MMM yyyy") : <span className="text-muted-foreground">Select date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                <Calendar mode="single" selected={deliveryDate} onSelect={setDeliveryDate} />
              </PopoverContent>
            </Popover>
          </CardContent>
        </Card>
      </div>

      {/* Reference Photo */}
      <Card className="luxury-card rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-serif text-base">Reference Photo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <label className="cursor-pointer">
              <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
              <div className="btn-gold-outline px-4 py-2 text-sm rounded-sm flex items-center gap-2">
                <Upload className="w-4 h-4" />
                {uploading ? "Uploading..." : "Upload Photo"}
              </div>
            </label>
            {referencePhoto && (
              <img src={`${BACKEND_URL}${referencePhoto}`} alt="Reference" className="w-16 h-16 object-cover rounded-sm border border-border" />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Submit */}
      <div className="flex flex-col sm:flex-row items-center gap-3 justify-end pb-8">
        <Button variant="outline" onClick={() => navigate("/slips")} className="rounded-sm border-border w-full sm:w-auto">Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving} className="btn-maroon rounded-sm px-8 w-full sm:w-auto">
          {saving ? "Creating..." : "Create Slip"}
        </Button>
      </div>
    </div>
  );
}
